package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.BorderStroke
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import kotlin.time.Clock
import kotlinx.coroutines.launch
import org.example.abigail.formatMillisToDate

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GestionBoletosView(viewModel: AdminViewModel) {
    val ciudadesDisponibles by viewModel.ciudades.collectAsState()
    val mapaAsientos by viewModel.mapaAsientos.collectAsState()
    val cargandoMapa by viewModel.cargandoMapa.collectAsState()

    var origenMapa by remember { mutableStateOf("") }
    var destinoMapa by remember { mutableStateOf("") }
    var fechaMapa by remember { mutableStateOf("") }
    var horarioMapa by remember { mutableStateOf("") }
    var horariosDisponibles by remember { mutableStateOf<List<String>>(emptyList()) }
    var buscandoHorarios by remember { mutableStateOf(false) }
    var mostrarCalendario by remember { mutableStateOf(false) }

    var expandedOrigen by remember { mutableStateOf(false) }
    var expandedDestino by remember { mutableStateOf(false) }
    var expandedHorario by remember { mutableStateOf(false) }

    var asientoSeleccionado by remember { mutableStateOf<AdminSeatInfo?>(null) }
    var mostrarConfirmCancelarAsiento by remember { mutableStateOf(false) }
    var reservaACancelarDesdeAsiento by remember { mutableStateOf<ReservaAdmin?>(null) }

    // Estado para reenvío de boletos desde el popup de asiento
    var reservaAReenviar by remember { mutableStateOf<ReservaAdmin?>(null) }
    var correoReenvio by remember { mutableStateOf("") }
    var enviandoBoletos by remember { mutableStateOf(false) }

    // Estado para reprogramación
    val reservasActivas by viewModel.reservasActivas.collectAsState()
    val reservasPasadas by viewModel.reservasPasadas.collectAsState()
    var reservaAReprogramar by remember { mutableStateOf<ReservaAdmin?>(null) }
    var mostrarMapaAsientos by remember { mutableStateOf(false) }
    var asientosOcupadosDestino by remember { mutableStateOf(listOf<Int>()) }
    var datosNuevoViaje by remember { mutableStateOf<Triple<String, String, String>?>(null) }
    var pasajeroEnTransito by remember { mutableStateOf<ReservaAdmin?>(null) }
    var mostrarSeleccionPasajeros by remember { mutableStateOf(false) }
    var pasajerosSeleccionadosParaMover by remember { mutableStateOf<List<PasajeroInfo>>(emptyList()) }
    var asientosEnVivo by remember { mutableStateOf(listOf<Int>()) }
    var mensajeSistema by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()
    val viajeSeleccionado = origenMapa.isNotBlank() && destinoMapa.isNotBlank() && fechaMapa.isNotBlank() && horarioMapa.isNotBlank()

    // Lista de viajes de los próximos 7 días (en vez de seleccionar manualmente)
    val itinerarios by viewModel.itinerarios.collectAsState()
    var busquedaViaje by remember { mutableStateOf("") }
    val hoyGB = org.example.abigail.obtenerFechaHoy()
    val diaHoyGB = fechaAEpochDay(hoyGB)
    val viajesProximos = itinerarios
        .filter { val d = fechaAEpochDay(it.fecha); d in diaHoyGB..(diaHoyGB + 7) }
        .filter { v -> busquedaViaje.isBlank() || v.ruta.contains(busquedaViaje, ignoreCase = true) || v.chofer.contains(busquedaViaje, ignoreCase = true) }
        .sortedWith(compareBy({ it.fecha }, { it.ruta }, { it.horario }))

    LaunchedEffect(Unit) { viewModel.cargarGestionDeBoletos(); viewModel.cargarItinerarios() }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp).verticalScroll(rememberScrollState()), horizontalAlignment = Alignment.CenterHorizontally) {

        // === HEADER ===
        Text("Gestión de Boletos", style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
        Text("Busca un pasajero o selecciona un viaje", color = Color.Gray, modifier = Modifier.padding(bottom = 16.dp))

        // === BUSCADOR DE PASAJEROS ===
        var terminoBusqueda by remember { mutableStateOf("") }
        val resultadosBusqueda by viewModel.resultadosBusqueda.collectAsState()
        val buscandoBoletos by viewModel.buscandoBoletos.collectAsState()

        Card(
            colors = CardDefaults.cardColors(containerColor = Color(0xFFF5F7FF)),
            shape = RoundedCornerShape(14.dp),
            modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                OutlinedTextField(
                    value = terminoBusqueda,
                    onValueChange = {
                        terminoBusqueda = it
                        if (it.length >= 2) viewModel.buscarBoletos(it)
                        else viewModel.limpiarBusqueda()
                    },
                    placeholder = { Text("Nombre, teléfono o correo del pasajero") },
                    leadingIcon = { Icon(Icons.Filled.Search, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(20.dp)) },
                    trailingIcon = {
                        if (terminoBusqueda.isNotBlank()) {
                            IconButton(onClick = { terminoBusqueda = ""; viewModel.limpiarBusqueda() }) {
                                Icon(Icons.Filled.Cancel, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(18.dp))
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = SmartVanColors.CyanAccent,
                        unfocusedBorderColor = SmartVanColors.Borde,
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White
                    )
                )

                if (buscandoBoletos) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                        CircularProgressIndicator(color = SmartVanColors.CyanAccent, modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Buscando...", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                    }
                } else if (terminoBusqueda.length >= 2 && resultadosBusqueda.isEmpty()) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Text("No se encontraron boletos para \"$terminoBusqueda\"", color = Color.Gray, style = MaterialTheme.typography.bodySmall, modifier = Modifier.fillMaxWidth(), textAlign = TextAlign.Center)
                } else if (resultadosBusqueda.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text("${resultadosBusqueda.size} resultado(s)", color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                    Spacer(modifier = Modifier.height(8.dp))

                    val puntosSubidaCatalogo by viewModel.puntosSubida.collectAsState()

                    resultadosBusqueda.take(20).forEach { resultado ->
                        val r = resultado.reserva
                        val p = resultado.pasajero
                        val esPasado = r.esPasado
                        val bgColor = if (esPasado) Color(0xFFFAFAFA) else Color.White
                        val indicadorColor = when {
                            r.viajeFueCancelado -> SmartVanColors.RojoFuerte
                            !r.pagoConfirmado -> SmartVanColors.Amarillo
                            esPasado -> Color(0xFFBDBDBD)
                            else -> SmartVanColors.Verde
                        }

                        var mostrarSelectorSubida by remember(r.idReserva) { mutableStateOf(false) }
                        var expandedSubida by remember(r.idReserva) { mutableStateOf(false) }
                        var guardandoSubida by remember(r.idReserva) { mutableStateOf(false) }

                        Card(
                            colors = CardDefaults.cardColors(containerColor = bgColor),
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                            border = BorderStroke(1.dp, SmartVanColors.Borde)
                        ) {
                            Column {
                                Row(modifier = Modifier.fillMaxWidth()) {
                                    // Indicador lateral de estado
                                    Box(modifier = Modifier.width(4.dp).height(100.dp).background(indicadorColor))

                                    Column(modifier = Modifier.padding(12.dp).weight(1f)) {
                                        // Nombre y asiento
                                        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                            Icon(Icons.Filled.Person, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(18.dp))
                                            Spacer(modifier = Modifier.width(6.dp))
                                            Text(p.nombre, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                                            Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.AzulOscuro), shape = RoundedCornerShape(6.dp)) {
                                                Text("Asiento ${p.asiento}", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall, modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp))
                                            }
                                        }

                                        Spacer(modifier = Modifier.height(4.dp))

                                        // Ruta y fecha
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(14.dp))
                                            Spacer(modifier = Modifier.width(4.dp))
                                            Text(r.ruta, color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Medium, style = MaterialTheme.typography.bodySmall)
                                        }
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Icon(Icons.Filled.CalendarToday, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp))
                                            Spacer(modifier = Modifier.width(4.dp))
                                            Text("${r.fecha} • ${r.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                                            if (r.metodoPago.isNotBlank()) {
                                                Spacer(modifier = Modifier.width(8.dp))
                                                Text(r.metodoPago.uppercase(), color = SmartVanColors.AzulOscuro, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                                            }
                                        }

                                        // Info de contacto
                                        if (p.telefono.isNotBlank() || p.correo.isNotBlank()) {
                                            Spacer(modifier = Modifier.height(2.dp))
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                if (p.telefono.isNotBlank()) {
                                                    Icon(Icons.Filled.Phone, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(12.dp))
                                                    Text(" ${p.telefono}", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                                    Spacer(modifier = Modifier.width(8.dp))
                                                }
                                                if (p.correo.isNotBlank()) {
                                                    Icon(Icons.Filled.Email, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(12.dp))
                                                    Text(" ${p.correo}", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                                }
                                            }
                                        }

                                        // Parada de bajada
                                        if (p.parada.isNotBlank()) {
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.RojoFuerte, modifier = Modifier.size(12.dp))
                                                Text(" Baja en: ${p.parada}", color = SmartVanColors.RojoFuerte, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Medium)
                                            }
                                        }

                                        // Punto de subida con botón de editar
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(12.dp))
                                            if (resultado.puntoSubida.isNotBlank()) {
                                                Text(" Sube en: ${resultado.puntoSubida}", color = SmartVanColors.Verde, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Medium)
                                            } else {
                                                Text(" Sin punto de subida", color = Color.Gray, style = MaterialTheme.typography.labelSmall, fontStyle = FontStyle.Italic)
                                            }
                                            if (!esPasado && !r.viajeFueCancelado) {
                                                Spacer(modifier = Modifier.width(4.dp))
                                                IconButton(
                                                    onClick = { mostrarSelectorSubida = !mostrarSelectorSubida },
                                                    modifier = Modifier.size(20.dp)
                                                ) {
                                                    Icon(Icons.Filled.Edit, contentDescription = "Cambiar subida", tint = SmartVanColors.Verde, modifier = Modifier.size(14.dp))
                                                }
                                            }
                                        }

                                        // Extras
                                        if (p.maletasExtras > 0 || p.llevaMascota) {
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                if (p.maletasExtras > 0) Text("Maletas extra: ${p.maletasExtras}  ", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                                if (p.llevaMascota) Text("Mascota", color = SmartVanColors.NaranjaDark, fontWeight = FontWeight.Medium, style = MaterialTheme.typography.labelSmall)
                                            }
                                        }

                                        // Status
                                        val statusLabel = when {
                                            r.viajeFueCancelado -> "Viaje cancelado"
                                            !r.pagoConfirmado -> "Pago pendiente"
                                            esPasado -> "Viaje pasado"
                                            else -> "Confirmado"
                                        }
                                        Spacer(modifier = Modifier.height(2.dp))
                                        Text(statusLabel, color = indicadorColor, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)

                                        // Reenviar boleto (con guard: solo si el pago está confirmado).
                                        // Si el pago está pendiente, la CF también rechaza (defensa dura).
                                        if (r.pagoConfirmado && p.correo.contains("@")) {
                                            Spacer(modifier = Modifier.height(6.dp))
                                            var enviandoRe by remember(r.idReserva, p.asiento) { mutableStateOf(false) }
                                            OutlinedButton(
                                                onClick = {
                                                    enviandoRe = true
                                                    viewModel.reenviarBoletos(r, p.correo) { _ -> enviandoRe = false }
                                                },
                                                modifier = Modifier.height(30.dp),
                                                contentPadding = PaddingValues(horizontal = 10.dp, vertical = 0.dp),
                                                enabled = !enviandoRe
                                            ) {
                                                Icon(Icons.Filled.Send, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(13.dp))
                                                Spacer(modifier = Modifier.width(4.dp))
                                                Text(if (enviandoRe) "Enviando..." else "Reenviar boleto", fontSize = 11.sp, color = SmartVanColors.AzulOscuro)
                                            }
                                        } else if (!r.pagoConfirmado) {
                                            Spacer(modifier = Modifier.height(4.dp))
                                            Text(
                                                "No se puede reenviar el boleto hasta confirmar el pago.",
                                                style = MaterialTheme.typography.labelSmall,
                                                color = Color(0xFFEF6C00),
                                                fontStyle = FontStyle.Italic
                                            )
                                        }
                                    }

                                    // Monto
                                    Column(modifier = Modifier.padding(top = 12.dp, end = 12.dp), horizontalAlignment = Alignment.End) {
                                        Text("$${r.totalPagado}", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.titleMedium)
                                        Text("MXN", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                    }
                                }

                                // Selector de punto de subida expandible
                                if (mostrarSelectorSubida && puntosSubidaCatalogo.isNotEmpty()) {
                                    HorizontalDivider(color = SmartVanColors.Borde)
                                    Column(modifier = Modifier.fillMaxWidth().background(Color(0xFFF0FFF0)).padding(12.dp)) {
                                        Text("Cambiar punto de subida:", fontWeight = FontWeight.Bold, color = SmartVanColors.VerdeOscuro, style = MaterialTheme.typography.labelSmall)
                                        Spacer(modifier = Modifier.height(8.dp))

                                        ExposedDropdownMenuBox(
                                            expanded = expandedSubida,
                                            onExpandedChange = { expandedSubida = it }
                                        ) {
                                            OutlinedTextField(
                                                value = resultado.puntoSubida.ifBlank { "Seleccionar punto de subida" },
                                                onValueChange = {},
                                                readOnly = true,
                                                modifier = Modifier.fillMaxWidth().menuAnchor(),
                                                textStyle = MaterialTheme.typography.bodySmall,
                                                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedSubida) },
                                                leadingIcon = { Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(16.dp)) },
                                                shape = RoundedCornerShape(10.dp),
                                                colors = OutlinedTextFieldDefaults.colors(
                                                    focusedBorderColor = SmartVanColors.Verde,
                                                    unfocusedBorderColor = SmartVanColors.Borde,
                                                    focusedContainerColor = Color.White,
                                                    unfocusedContainerColor = Color.White
                                                ),
                                                singleLine = true
                                            )
                                            ExposedDropdownMenu(
                                                expanded = expandedSubida,
                                                onDismissRequest = { expandedSubida = false }
                                            ) {
                                                puntosSubidaCatalogo.forEach { punto ->
                                                    val seleccionado = punto == resultado.puntoSubida
                                                    DropdownMenuItem(
                                                        text = {
                                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                                if (seleccionado) {
                                                                    Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(14.dp))
                                                                    Spacer(modifier = Modifier.width(6.dp))
                                                                }
                                                                Text(punto, fontWeight = if (seleccionado) FontWeight.Bold else FontWeight.Normal, style = MaterialTheme.typography.bodySmall)
                                                            }
                                                        },
                                                        onClick = {
                                                            expandedSubida = false
                                                            if (punto != resultado.puntoSubida) {
                                                                guardandoSubida = true
                                                                viewModel.cambiarPuntoSubida(r, punto) { exito ->
                                                                    guardandoSubida = false
                                                                    if (exito) mostrarSelectorSubida = false
                                                                }
                                                            }
                                                        }
                                                    )
                                                }
                                            }
                                        }

                                        if (guardandoSubida) {
                                            Spacer(modifier = Modifier.height(6.dp))
                                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                                                CircularProgressIndicator(color = SmartVanColors.Verde, modifier = Modifier.size(16.dp), strokeWidth = 2.dp)
                                                Spacer(modifier = Modifier.width(6.dp))
                                                Text("Guardando...", color = SmartVanColors.Verde, style = MaterialTheme.typography.labelSmall)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (resultadosBusqueda.size > 20) {
                        Spacer(modifier = Modifier.height(4.dp))
                        Text("Mostrando 20 de ${resultadosBusqueda.size} resultados. Refina tu búsqueda.", color = Color.Gray, style = MaterialTheme.typography.labelSmall, modifier = Modifier.fillMaxWidth(), textAlign = TextAlign.Center)
                    }
                }
            }
        }

        HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), color = SmartVanColors.Borde)

        // === VIAJES DE LOS PRÓXIMOS 7 DÍAS ===
        if (!viajeSeleccionado) {
            OutlinedTextField(
                value = busquedaViaje,
                onValueChange = { busquedaViaje = it },
                modifier = Modifier.fillMaxWidth(),
                placeholder = { Text("Filtrar por ruta o chofer...") },
                leadingIcon = { Icon(Icons.Filled.Search, contentDescription = null, tint = SmartVanColors.CyanAccent) },
                shape = RoundedCornerShape(12.dp),
                singleLine = true
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text("Toca un viaje para ver el layout del transporte y los pasajeros.", color = Color.Gray, style = MaterialTheme.typography.bodySmall, modifier = Modifier.fillMaxWidth())
            Spacer(modifier = Modifier.height(8.dp))
            if (viajesProximos.isEmpty()) {
                Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoNaranja), shape = RoundedCornerShape(12.dp), modifier = Modifier.fillMaxWidth()) {
                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.Warning, contentDescription = null, tint = SmartVanColors.NaranjaDark)
                        Spacer(modifier = Modifier.width(12.dp))
                        Text("No hay viajes programados en los próximos 7 días.", color = SmartVanColors.NaranjaDark, fontWeight = FontWeight.Medium)
                    }
                }
            } else {
                viajesProximos.groupBy { it.fecha }.forEach { (fecha, viajesDia) ->
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(top = 8.dp, bottom = 4.dp)) {
                        Icon(Icons.Filled.CalendarToday, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(fecha, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("${viajesDia.size} viaje(s)", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                    }
                    viajesDia.forEach { viaje ->
                        val partes = viaje.ruta.split(" - ").map { it.trim() }
                        Card(
                            colors = CardDefaults.cardColors(containerColor = Color.White),
                            shape = RoundedCornerShape(12.dp),
                            border = BorderStroke(1.dp, SmartVanColors.Borde),
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp).clickable {
                                origenMapa = partes.getOrNull(0) ?: viaje.ruta
                                destinoMapa = partes.getOrNull(1) ?: ""
                                fechaMapa = viaje.fecha
                                horarioMapa = viaje.horario
                                viewModel.cargarMapaAsientos(viaje.ruta, viaje.fecha, viaje.horario)
                            }
                        ) {
                            Column(modifier = Modifier.padding(14.dp)) {
                                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                    Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(18.dp))
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text(viaje.ruta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, modifier = Modifier.weight(1f))
                                    Icon(Icons.Filled.ChevronRight, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(20.dp))
                                }
                                Spacer(modifier = Modifier.height(4.dp))
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Filled.Schedule, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(14.dp))
                                    Spacer(modifier = Modifier.width(4.dp))
                                    Text(viaje.horario, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.SemiBold)
                                    if (viaje.capacidad > 0) {
                                        Spacer(modifier = Modifier.width(10.dp))
                                        Icon(Icons.Filled.EventSeat, contentDescription = null, tint = SmartVanColors.VerdeOscuro, modifier = Modifier.size(14.dp))
                                        Spacer(modifier = Modifier.width(4.dp))
                                        Text("${viaje.vendidos}/${viaje.capacidad} vendidos", color = SmartVanColors.VerdeOscuro, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.SemiBold)
                                    }
                                }
                                if (viaje.chofer.isNotBlank() && viaje.chofer != "Sin asignar") {
                                    Spacer(modifier = Modifier.height(2.dp))
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Filled.Person, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(13.dp))
                                        Spacer(modifier = Modifier.width(4.dp))
                                        Text(viaje.chofer, color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        } else {
            // Viaje abierto: encabezado con botón volver
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.AzulOscuro),
                shape = RoundedCornerShape(14.dp),
                modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)
            ) {
                Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = {
                        origenMapa = ""; destinoMapa = ""; fechaMapa = ""; horarioMapa = ""
                    }) {
                        Icon(Icons.Filled.ArrowBack, contentDescription = "Volver a la lista", tint = Color.White)
                    }
                    Spacer(modifier = Modifier.width(4.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text("$origenMapa → $destinoMapa", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
                        Text("$fechaMapa | $horarioMapa", color = Color.White.copy(alpha = 0.75f), style = MaterialTheme.typography.bodySmall)
                    }
                }
            }
        }

        // === MAPA DE ASIENTOS ===
        if (viajeSeleccionado) {
            // Leyenda
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp), modifier = Modifier.padding(bottom = 12.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) { Box(modifier = Modifier.size(16.dp).background(SmartVanColors.Verde, RoundedCornerShape(4.dp))); Spacer(modifier = Modifier.width(4.dp)); Text("Libre", style = MaterialTheme.typography.bodySmall) }
                Row(verticalAlignment = Alignment.CenterVertically) { Box(modifier = Modifier.size(16.dp).background(Color(0xFFBDBDBD), RoundedCornerShape(4.dp))); Spacer(modifier = Modifier.width(4.dp)); Text("Pagado", style = MaterialTheme.typography.bodySmall) }
                Row(verticalAlignment = Alignment.CenterVertically) { Box(modifier = Modifier.size(16.dp).background(SmartVanColors.Amarillo, RoundedCornerShape(4.dp))); Spacer(modifier = Modifier.width(4.dp)); Text("Pendiente", style = MaterialTheme.typography.bodySmall) }
            }

            if (cargandoMapa) {
                Box(modifier = Modifier.fillMaxWidth().height(300.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = SmartVanColors.CyanAccent)
                }
            } else if (mapaAsientos.isNotEmpty()) {
                // Cargar layout del vehículo del viaje
                val layoutViaje by viewModel.layoutViaje.collectAsState()
                LaunchedEffect("$origenMapa - $destinoMapa-$fechaMapa-$horarioMapa") {
                    viewModel.cargarLayoutDeViaje("$origenMapa - $destinoMapa", fechaMapa, horarioMapa)
                }

                // Layout default (13 asientos) si no hay configuración guardada
                val defaultLayout = VehiculoConfig(
                    nombre = "Default", capacidad = 13, columnas = 4,
                    filas = listOf(
                        FilaVehiculo(listOf(-1, 0, 0, 1)),
                        FilaVehiculo(listOf(2, 3, 0, 4)),
                        FilaVehiculo(listOf(5, 6, 0, 7)),
                        FilaVehiculo(listOf(8, 9, 0, 10)),
                        FilaVehiculo(listOf(11, 12, 13, 0)),
                    )
                )
                val layout = layoutViaje ?: defaultLayout

                MapaAsientosDinamico(
                    layout = layout,
                    asientosInfo = mapaAsientos,
                    onAsientoClick = { asientoSeleccionado = it }
                )

                // Resumen
                Spacer(modifier = Modifier.height(12.dp))
                val libres = mapaAsientos.count { it.status == AdminSeatStatus.LIBRE }
                val pagados = mapaAsientos.count { it.status == AdminSeatStatus.PAGADO }
                val congelados = mapaAsientos.count { it.status == AdminSeatStatus.CONGELADO }
                Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoGrisClaro), shape = RoundedCornerShape(12.dp), modifier = Modifier.fillMaxWidth()) {
                    Row(modifier = Modifier.fillMaxWidth().padding(12.dp), horizontalArrangement = Arrangement.SpaceEvenly) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) { Text("$libres", fontWeight = FontWeight.Black, color = SmartVanColors.Verde, style = MaterialTheme.typography.titleLarge); Text("Libres", color = Color.Gray, style = MaterialTheme.typography.labelSmall) }
                        Column(horizontalAlignment = Alignment.CenterHorizontally) { Text("$pagados", fontWeight = FontWeight.Black, color = Color(0xFF757575), style = MaterialTheme.typography.titleLarge); Text("Pagados", color = Color.Gray, style = MaterialTheme.typography.labelSmall) }
                        Column(horizontalAlignment = Alignment.CenterHorizontally) { Text("$congelados", fontWeight = FontWeight.Black, color = SmartVanColors.NaranjaDark, style = MaterialTheme.typography.titleLarge); Text("Pendientes", color = Color.Gray, style = MaterialTheme.typography.labelSmall) }
                    }
                }

                // Botón actualizar
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedButton(onClick = { viewModel.cargarMapaAsientos("$origenMapa - $destinoMapa", fechaMapa, horarioMapa) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(10.dp)) {
                    Icon(Icons.Filled.Search, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(modifier = Modifier.width(6.dp))
                    Text("Actualizar mapa")
                }

                // ═══ Pasajeros por Punto de Subida ═══
                val rutaViaje = "$origenMapa - $destinoMapa"
                val reservasDeEsteViaje = reservasActivas.filter { it.ruta == rutaViaje && it.fecha == fechaMapa && it.horario == horarioMapa }
                val puntosSubidaCatalogoViaje by viewModel.puntosSubida.collectAsState()
                val puntosSubidaInfoViaje by viewModel.puntosSubidaInfo.collectAsState()

                if (reservasDeEsteViaje.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(16.dp))

                    // Agrupar por punto de subida
                    data class PasajeroConSubida(val pasajero: PasajeroInfo, val puntoSubida: String, val reserva: ReservaAdmin)
                    val todosConSubida = reservasDeEsteViaje.flatMap { reserva ->
                        reserva.pasajeros.map { p -> PasajeroConSubida(p, reserva.puntoSubida, reserva) }
                    }
                    val puntosUnicos = todosConSubida.map { it.puntoSubida.ifBlank { "Sin asignar" } }.distinct().sorted()

                    var filtroSubidaViaje by remember { mutableStateOf("Todos") }

                    Card(
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFF5F7FF)),
                        shape = RoundedCornerShape(14.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(20.dp))
                                Spacer(modifier = Modifier.width(8.dp))
                                Text("Pasajeros por Punto de Subida", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.titleSmall)
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("${todosConSubida.size} pasajero(s) en este viaje", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            Spacer(modifier = Modifier.height(10.dp))

                            // Filter chips
                            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                val opciones = listOf("Todos") + puntosUnicos
                                opciones.forEach { opcion ->
                                    val seleccionado = filtroSubidaViaje == opcion
                                    val count = if (opcion == "Todos") todosConSubida.size else todosConSubida.count { (it.puntoSubida.ifBlank { "Sin asignar" }) == opcion }
                                    FilterChip(
                                        selected = seleccionado,
                                        onClick = { filtroSubidaViaje = opcion },
                                        label = { Text("$opcion ($count)", style = MaterialTheme.typography.labelSmall) },
                                        leadingIcon = if (seleccionado) { { Icon(Icons.Filled.CheckCircle, contentDescription = null, modifier = Modifier.size(14.dp)) } } else null,
                                        colors = FilterChipDefaults.filterChipColors(
                                            selectedContainerColor = SmartVanColors.Verde,
                                            selectedLabelColor = Color.White,
                                            selectedLeadingIconColor = Color.White
                                        ),
                                        modifier = Modifier.padding(vertical = 2.dp)
                                    )
                                }
                            }

                            Spacer(modifier = Modifier.height(10.dp))

                            // Pasajeros filtrados
                            val filtrados = if (filtroSubidaViaje == "Todos") todosConSubida else todosConSubida.filter { (it.puntoSubida.ifBlank { "Sin asignar" }) == filtroSubidaViaje }

                            filtrados.forEach { item ->
                                val infoParada = puntosSubidaInfoViaje.find { it.nombre == item.puntoSubida }
                                Card(
                                    colors = CardDefaults.cardColors(containerColor = Color.White),
                                    shape = RoundedCornerShape(8.dp),
                                    modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp),
                                    border = BorderStroke(1.dp, SmartVanColors.Borde)
                                ) {
                                    Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                                        // Indicador de subida
                                        Box(modifier = Modifier.width(3.dp).height(50.dp).background(if (item.puntoSubida.isNotBlank()) SmartVanColors.Verde else Color(0xFFBDBDBD)))
                                        Spacer(modifier = Modifier.width(10.dp))
                                        Column(modifier = Modifier.weight(1f)) {
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                Text(item.pasajero.nombre, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodySmall)
                                                Spacer(modifier = Modifier.width(6.dp))
                                                Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.AzulOscuro), shape = RoundedCornerShape(4.dp)) {
                                                    Text("A${item.pasajero.asiento}", color = Color.White, style = MaterialTheme.typography.labelSmall, modifier = Modifier.padding(horizontal = 6.dp, vertical = 1.dp))
                                                }
                                            }
                                            if (item.pasajero.telefono.isNotBlank()) {
                                                Text(item.pasajero.telefono, color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                            }
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(10.dp))
                                                Spacer(modifier = Modifier.width(4.dp))
                                                Text(
                                                    item.puntoSubida.ifBlank { "Sin asignar" },
                                                    color = if (item.puntoSubida.isNotBlank()) SmartVanColors.Verde else Color.Gray,
                                                    fontWeight = FontWeight.Medium,
                                                    style = MaterialTheme.typography.labelSmall
                                                )
                                                if (infoParada != null && infoParada.hora.isNotBlank()) {
                                                    Text("  •  ${infoParada.hora}", color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                                                }
                                            }
                                            if (item.pasajero.parada.isNotBlank()) {
                                                Row(verticalAlignment = Alignment.CenterVertically) {
                                                    Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.RojoFuerte, modifier = Modifier.size(10.dp))
                                                    Spacer(modifier = Modifier.width(4.dp))
                                                    Text("Baja: ${item.pasajero.parada}", color = SmartVanColors.RojoFuerte, style = MaterialTheme.typography.labelSmall)
                                                }
                                            }
                                        }
                                        // Pago status
                                        val pagoColor = if (item.reserva.pagoConfirmado) SmartVanColors.Verde else SmartVanColors.NaranjaDark
                                        val pagoTexto = if (item.reserva.pagoConfirmado) "Pagado" else "Pendiente"
                                        Text(pagoTexto, color = pagoColor, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                                    }
                                }
                            }
                        }
                    }
                }

                // ═══ Conflictos de asientos pendientes ═══
                val conflictosPendientes by viewModel.conflictos.collectAsState()
                val cargandoConflictos by viewModel.cargandoConflictos.collectAsState()
                LaunchedEffect("$origenMapa - $destinoMapa-$fechaMapa-$horarioMapa") { viewModel.cargarConflictos() }

                val conflictosDeEsteViaje = conflictosPendientes.filter {
                    it.ruta == "$origenMapa - $destinoMapa" && it.fecha == fechaMapa && it.horario == horarioMapa
                }

                if (conflictosDeEsteViaje.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(16.dp))
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                        border = BorderStroke(2.dp, SmartVanColors.NaranjaDark),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.Warning, contentDescription = null, tint = SmartVanColors.NaranjaDark, modifier = Modifier.size(22.dp))
                                Spacer(modifier = Modifier.width(8.dp))
                                Text("Conflictos de Asiento", fontWeight = FontWeight.Black, color = SmartVanColors.NaranjaDeep, style = MaterialTheme.typography.titleMedium)
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("Pagos tardíos con asientos que ya se vendieron. Reasigna a otro asiento libre o procesa reembolso.", color = Color(0xFF795548), style = MaterialTheme.typography.bodySmall)
                            Spacer(modifier = Modifier.height(10.dp))

                            conflictosDeEsteViaje.forEach { conflicto ->
                                ConflictoAsientoCard(
                                    conflicto = conflicto,
                                    asientosLibresDelViaje = mapaAsientos.filter { it.status == AdminSeatStatus.LIBRE }.map { it.asiento },
                                    onReasignar = { asientoViejo, asientoNuevo, pasajero ->
                                        viewModel.reasignarAsiento(conflicto.id, asientoViejo, asientoNuevo, pasajero) { exito ->
                                            if (exito) viewModel.cargarMapaAsientos("$origenMapa - $destinoMapa", fechaMapa, horarioMapa)
                                        }
                                    },
                                    onReembolsar = {
                                        viewModel.marcarReembolsado(conflicto.id) {}
                                    }
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                            }
                        }
                    }
                }
            }
        } else if (origenMapa.isNotBlank() && destinoMapa.isNotBlank() && fechaMapa.isNotBlank()) {
            if (buscandoHorarios) {
                Text("Buscando horarios programados...", color = Color.Gray, fontStyle = FontStyle.Italic)
            } else if (horariosDisponibles.isEmpty()) {
                Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoNaranja), shape = RoundedCornerShape(12.dp), modifier = Modifier.fillMaxWidth()) {
                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.Warning, contentDescription = null, tint = SmartVanColors.NaranjaDark)
                        Spacer(modifier = Modifier.width(12.dp))
                        Text("No hay viajes programados para esta ruta y fecha.", color = SmartVanColors.NaranjaDark, fontWeight = FontWeight.Medium)
                    }
                }
            }
        }
    }

    // === DIALOG DE DETALLE DE ASIENTO ===
    if (asientoSeleccionado != null) {
        val seat = asientoSeleccionado!!
        val statusText = when (seat.status) { AdminSeatStatus.LIBRE -> "Disponible"; AdminSeatStatus.PAGADO -> "Pago Confirmado"; AdminSeatStatus.CONGELADO -> "Pago en Proceso" }
        val statusColor = when (seat.status) { AdminSeatStatus.LIBRE -> SmartVanColors.Verde; AdminSeatStatus.PAGADO -> Color(0xFF757575); AdminSeatStatus.CONGELADO -> SmartVanColors.Amarillo }

        AlertDialog(
            onDismissRequest = { asientoSeleccionado = null },
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.EventSeat, contentDescription = null, tint = statusColor, modifier = Modifier.size(28.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text("Asiento ${seat.asiento}", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
                        Text(statusText, color = statusColor, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                    }
                }
            },
            text = {
                Column {
                    if (seat.pasajero != null) {
                        Card(colors = CardDefaults.cardColors(containerColor = Color(0xFFF5F7FF)), shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth()) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                Text(seat.pasajero.nombre, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.titleMedium)
                                Spacer(modifier = Modifier.height(6.dp))
                                if (seat.pasajero.telefono.isNotBlank()) { Row(verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Filled.Phone, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp)); Spacer(modifier = Modifier.width(6.dp)); Text(seat.pasajero.telefono, color = Color.Gray, style = MaterialTheme.typography.bodySmall) } }
                                if (seat.pasajero.correo.isNotBlank()) { Row(verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Filled.Email, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp)); Spacer(modifier = Modifier.width(6.dp)); Text(seat.pasajero.correo, color = Color.Gray, style = MaterialTheme.typography.bodySmall) } }
                                if (seat.pasajero.parada.isNotBlank()) { Row(verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Filled.LocationOn, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp)); Spacer(modifier = Modifier.width(6.dp)); Text("Baja en: ${seat.pasajero.parada}", color = Color.Gray, style = MaterialTheme.typography.bodySmall) } }
                                if (seat.pasajero.maletasExtras > 0) { Text("Maletas extra: ${seat.pasajero.maletasExtras}", color = Color.Gray, style = MaterialTheme.typography.bodySmall) }
                                if (seat.pasajero.llevaMascota) { Text("Lleva mascota", color = SmartVanColors.NaranjaDark, fontWeight = FontWeight.Medium, style = MaterialTheme.typography.bodySmall) }
                            }
                        }

                        if (seat.metodoPago.isNotBlank()) {
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.AttachMoney, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(16.dp))
                                Spacer(modifier = Modifier.width(4.dp))
                                Text("Método: ${seat.metodoPago.uppercase()}", color = SmartVanColors.AzulOscuro, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                            }
                        }

                        if (seat.status == AdminSeatStatus.CONGELADO && seat.expiraEn > 0L) {
                            Spacer(modifier = Modifier.height(4.dp))
                            val tiempoActual = Clock.System.now().toEpochMilliseconds()
                            val restante = seat.expiraEn - tiempoActual
                            val minutosRestantes = (restante / 60000L).toInt()
                            val textoExpira = if (minutosRestantes > 60) "${minutosRestantes / 60}h ${minutosRestantes % 60}min restantes" else if (minutosRestantes > 0) "$minutosRestantes min restantes" else "Próximo a expirar"
                            Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo), shape = RoundedCornerShape(8.dp), modifier = Modifier.fillMaxWidth()) {
                                Row(modifier = Modifier.padding(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Filled.Timer, contentDescription = null, tint = SmartVanColors.NaranjaDark, modifier = Modifier.size(16.dp))
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text(textoExpira, color = SmartVanColors.NaranjaDark, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                                }
                            }
                        }

                        // Buscar reserva para acciones
                        if (seat.status == AdminSeatStatus.PAGADO) {
                            Spacer(modifier = Modifier.height(12.dp))
                            val reservaDelAsiento = reservasActivas.find { r ->
                                r.ruta == "$origenMapa - $destinoMapa" && r.fecha == fechaMapa && r.horario == horarioMapa &&
                                r.pasajeros.any { it.asiento == seat.asiento }
                            }
                            if (reservaDelAsiento != null) {
                                // ── Asignar / cambiar PARADA de bajada de este pasajero ──
                                val paradasCatalogo by viewModel.paradas.collectAsState()
                                var expandedParada by remember(seat.asiento) { mutableStateOf(false) }
                                var guardandoParada by remember(seat.asiento) { mutableStateOf(false) }
                                val paradaActual = seat.pasajero?.parada ?: ""
                                if (paradasCatalogo.isNotEmpty()) {
                                    Text("Parada de bajada:", fontWeight = FontWeight.SemiBold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.labelSmall)
                                    Spacer(modifier = Modifier.height(4.dp))
                                    ExposedDropdownMenuBox(expanded = expandedParada, onExpandedChange = { expandedParada = it }) {
                                        OutlinedTextField(
                                            value = paradaActual.ifBlank { "Sin asignar — toca para elegir" },
                                            onValueChange = {}, readOnly = true,
                                            modifier = Modifier.fillMaxWidth().menuAnchor(),
                                            textStyle = MaterialTheme.typography.bodySmall,
                                            leadingIcon = { Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.RojoFuerte, modifier = Modifier.size(16.dp)) },
                                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedParada) },
                                            shape = RoundedCornerShape(10.dp),
                                            singleLine = true,
                                            colors = OutlinedTextFieldDefaults.colors(focusedContainerColor = Color.White, unfocusedContainerColor = Color.White)
                                        )
                                        ExposedDropdownMenu(expanded = expandedParada, onDismissRequest = { expandedParada = false }) {
                                            paradasCatalogo.forEach { par ->
                                                DropdownMenuItem(
                                                    text = { Text(par, style = MaterialTheme.typography.bodySmall) },
                                                    onClick = {
                                                        expandedParada = false
                                                        if (par != paradaActual) {
                                                            guardandoParada = true
                                                            viewModel.cambiarParadaPasajero(reservaDelAsiento, seat.asiento, par) { exito ->
                                                                guardandoParada = false
                                                                if (exito) asientoSeleccionado = null
                                                            }
                                                        }
                                                    }
                                                )
                                            }
                                        }
                                    }
                                    if (guardandoParada) {
                                        Spacer(modifier = Modifier.height(4.dp))
                                        Text("Guardando bajada…", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                    }
                                    Spacer(modifier = Modifier.height(8.dp))
                                }
                                // ── Asignar / cambiar PUNTO DE SUBIDA de la reserva ──
                                val puntosSubidaCatalogoSeat by viewModel.puntosSubida.collectAsState()
                                var expandedSubidaSeat by remember(seat.asiento) { mutableStateOf(false) }
                                var guardandoSubidaSeat by remember(seat.asiento) { mutableStateOf(false) }
                                val subidaActual = reservaDelAsiento.puntoSubida
                                if (puntosSubidaCatalogoSeat.isNotEmpty()) {
                                    Text("Punto de subida:", fontWeight = FontWeight.SemiBold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.labelSmall)
                                    Spacer(modifier = Modifier.height(4.dp))
                                    ExposedDropdownMenuBox(expanded = expandedSubidaSeat, onExpandedChange = { expandedSubidaSeat = it }) {
                                        OutlinedTextField(
                                            value = subidaActual.ifBlank { "Sin asignar — toca para elegir" },
                                            onValueChange = {}, readOnly = true,
                                            modifier = Modifier.fillMaxWidth().menuAnchor(),
                                            textStyle = MaterialTheme.typography.bodySmall,
                                            leadingIcon = { Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(16.dp)) },
                                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedSubidaSeat) },
                                            shape = RoundedCornerShape(10.dp),
                                            singleLine = true,
                                            colors = OutlinedTextFieldDefaults.colors(focusedContainerColor = Color.White, unfocusedContainerColor = Color.White)
                                        )
                                        ExposedDropdownMenu(expanded = expandedSubidaSeat, onDismissRequest = { expandedSubidaSeat = false }) {
                                            puntosSubidaCatalogoSeat.forEach { punto ->
                                                DropdownMenuItem(
                                                    text = { Text(punto, style = MaterialTheme.typography.bodySmall) },
                                                    onClick = {
                                                        expandedSubidaSeat = false
                                                        if (punto != subidaActual) {
                                                            guardandoSubidaSeat = true
                                                            viewModel.cambiarPuntoSubida(reservaDelAsiento, punto) { exito ->
                                                                guardandoSubidaSeat = false
                                                                if (exito) asientoSeleccionado = null
                                                            }
                                                        }
                                                    }
                                                )
                                            }
                                        }
                                    }
                                    if (guardandoSubidaSeat) {
                                        Spacer(modifier = Modifier.height(4.dp))
                                        Text("Guardando subida…", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                    }
                                    Spacer(modifier = Modifier.height(8.dp))
                                }
                                // Descargar PDF del pasajero de este asiento
                                val pasajeroAsiento = seat.pasajero
                                if (pasajeroAsiento != null) {
                                    Button(
                                        onClick = {
                                            scope.launch {
                                                // QR real de verificaciones (si no, el chofer lo lee como ajeno)
                                                // y puntoSubida de la reserva (si no, el boleto dice "Terminal").
                                                val qrReal = viewModel.codigoQrDeAsiento(reservaDelAsiento, seat.asiento)
                                                val precioUnit = reservaDelAsiento.totalPagado / reservaDelAsiento.asientosComprados.coerceAtLeast(1)
                                                val boleto = Boleto(
                                                    asientos = listOf(seat.asiento),
                                                    pasajeros = listOf(pasajeroAsiento),
                                                    totalPagado = precioUnit,
                                                    fechaViaje = reservaDelAsiento.fecha,
                                                    horario = reservaDelAsiento.horario,
                                                    origenDestino = reservaDelAsiento.ruta,
                                                    puntoSubida = reservaDelAsiento.puntoSubida,
                                                    codigoQR = qrReal,
                                                    metodoPago = reservaDelAsiento.metodoPago,
                                                    pagoConfirmado = reservaDelAsiento.pagoConfirmado,
                                                    timestamp = reservaDelAsiento.idReserva
                                                )
                                                org.example.abigail.imprimirBoleto(pasajeroAsiento, boleto, precioUnit)
                                            }
                                        },
                                        modifier = Modifier.fillMaxWidth(),
                                        colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde),
                                        shape = RoundedCornerShape(10.dp)
                                    ) {
                                        Icon(Icons.Filled.FileDownload, contentDescription = null, modifier = Modifier.size(16.dp))
                                        Spacer(modifier = Modifier.width(6.dp))
                                        Text("Descargar PDF")
                                    }
                                    Spacer(modifier = Modifier.height(6.dp))
                                }
                                Button(
                                    onClick = {
                                        reservaAReenviar = reservaDelAsiento
                                        correoReenvio = reservaDelAsiento.pasajeros.firstOrNull()?.correo ?: ""
                                        asientoSeleccionado = null
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                    colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.CyanAccent),
                                    shape = RoundedCornerShape(10.dp)
                                ) {
                                    Icon(Icons.Filled.Email, contentDescription = null, modifier = Modifier.size(16.dp))
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text("Reenviar Boletos")
                                }
                                Spacer(modifier = Modifier.height(6.dp))
                                OutlinedButton(
                                    onClick = {
                                        reservaAReprogramar = reservaDelAsiento
                                        if (reservaDelAsiento.asientosComprados > 1) mostrarSeleccionPasajeros = true else pasajerosSeleccionadosParaMover = emptyList()
                                        asientoSeleccionado = null
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(10.dp)
                                ) {
                                    Icon(Icons.Filled.Edit, contentDescription = null, modifier = Modifier.size(16.dp), tint = SmartVanColors.AzulOscuro)
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text("Reprogramar", color = SmartVanColors.AzulOscuro)
                                }
                                Spacer(modifier = Modifier.height(6.dp))
                                // Botón cancelar boleto
                                OutlinedButton(
                                    onClick = {
                                        reservaACancelarDesdeAsiento = reservaDelAsiento
                                        asientoSeleccionado = null
                                        mostrarConfirmCancelarAsiento = true
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(10.dp),
                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = Color.Red),
                                    border = BorderStroke(1.dp, Color.Red)
                                ) {
                                    Icon(Icons.Filled.Cancel, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color.Red)
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text("Cancelar Boleto", color = Color.Red)
                                }
                            }
                        }
                    } else if (seat.status == AdminSeatStatus.LIBRE) {
                        Text("Este asiento está disponible para la venta.", color = Color.Gray, fontStyle = FontStyle.Italic)
                    }
                }
            },
            confirmButton = { TextButton(onClick = { asientoSeleccionado = null }) { Text("Cerrar", color = SmartVanColors.AzulOscuro) } }
        )
    }

    // Diálogo de confirmación de cancelación desde mapa de asientos
    if (mostrarConfirmCancelarAsiento && reservaACancelarDesdeAsiento != null) {
        val reservaCancel = reservaACancelarDesdeAsiento!!
        AlertDialog(
            onDismissRequest = { mostrarConfirmCancelarAsiento = false; reservaACancelarDesdeAsiento = null },
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Cancel, contentDescription = null, tint = Color.Red, modifier = Modifier.size(24.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Cancelar Boleto", fontWeight = FontWeight.Black, color = Color.Red)
                }
            },
            text = {
                Column {
                    Text("Se liberarán los asientos y se registrará para reembolso.", color = Color.DarkGray)
                    Spacer(modifier = Modifier.height(10.dp))
                    Card(colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF5F5)), shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text("Ruta: ${reservaCancel.ruta}", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                            Text("${reservaCancel.fecha} • ${reservaCancel.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            Spacer(modifier = Modifier.height(6.dp))
                            reservaCancel.pasajeros.forEach { p ->
                                Text("• ${p.nombre} — Asiento ${p.asiento}", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                            }
                            Spacer(modifier = Modifier.height(6.dp))
                            Text("Total a reembolsar: $${reservaCancel.totalPagado} MXN", color = Color.Red, fontWeight = FontWeight.Bold)
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        viewModel.cancelarBoleto(reservaCancel) { exito ->
                            if (exito) {
                                viewModel.cargarMapaAsientos("$origenMapa - $destinoMapa", fechaMapa, horarioMapa)
                            }
                        }
                        mostrarConfirmCancelarAsiento = false
                        reservaACancelarDesdeAsiento = null
                        asientoSeleccionado = null
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color.Red)
                ) { Text("Confirmar Cancelación") }
            },
            dismissButton = {
                TextButton(onClick = { mostrarConfirmCancelarAsiento = false; reservaACancelarDesdeAsiento = null }) { Text("No cancelar") }
            }
        )
    }

    // Diálogo de reenvío de boletos
    if (reservaAReenviar != null) {
        AlertDialog(
            onDismissRequest = { if (!enviandoBoletos) { reservaAReenviar = null } },
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Email, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(24.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Reenviar Boletos", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
                }
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoGrisClaro),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text(reservaAReenviar!!.ruta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                            Text("${reservaAReenviar!!.fecha} • ${reservaAReenviar!!.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            Spacer(modifier = Modifier.height(6.dp))
                            reservaAReenviar!!.pasajeros.forEach { p ->
                                Text("• ${p.nombre} (Asiento ${p.asiento})", style = MaterialTheme.typography.bodySmall)
                            }
                            Text("Total: $${reservaAReenviar!!.totalPagado} MXN", fontWeight = FontWeight.Bold, modifier = Modifier.padding(top = 4.dp))
                        }
                    }

                    val correoOriginal = reservaAReenviar!!.pasajeros.firstOrNull()?.correo ?: ""
                    if (correoOriginal.isNotBlank()) {
                        Card(
                            colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                            shape = RoundedCornerShape(8.dp)
                        ) {
                            Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.Info, contentDescription = null, tint = Color(0xFFF57F17), modifier = Modifier.size(16.dp))
                                Spacer(modifier = Modifier.width(8.dp))
                                Column {
                                    Text("Correo registrado por el cliente:", style = MaterialTheme.typography.labelSmall, color = Color(0xFFF57F17))
                                    Text(correoOriginal, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall, color = SmartVanColors.AzulOscuro)
                                }
                            }
                        }
                    }

                    OutlinedTextField(
                        value = correoReenvio,
                        onValueChange = { correoReenvio = it },
                        label = { Text("Enviar boletos a") },
                        placeholder = { Text("correo@ejemplo.com") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        leadingIcon = { Icon(Icons.Filled.Email, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(18.dp)) },
                        isError = correoReenvio.isNotBlank() && !correoReenvio.contains("@"),
                        supportingText = {
                            if (correoReenvio.isNotBlank() && !correoReenvio.contains("@")) {
                                Text("Ingresa un correo valido", color = Color.Red)
                            } else if (correoOriginal.isNotBlank() && correoReenvio != correoOriginal) {
                                Text("Correo modificado (original: $correoOriginal)", color = Color(0xFFF57F17), style = MaterialTheme.typography.labelSmall)
                            }
                        }
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        enviandoBoletos = true
                        viewModel.reenviarBoletos(reservaAReenviar!!, correoReenvio) {
                            enviandoBoletos = false
                            reservaAReenviar = null
                        }
                    },
                    enabled = correoReenvio.contains("@") && !enviandoBoletos,
                    colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.CyanAccent)
                ) {
                    if (enviandoBoletos) {
                        CircularProgressIndicator(color = Color.White, modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Enviando...")
                    } else {
                        Icon(Icons.Filled.Email, contentDescription = null, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text("Reenviar")
                    }
                }
            },
            dismissButton = {
                if (!enviandoBoletos) {
                    TextButton(onClick = { reservaAReenviar = null }) { Text("Cancelar") }
                }
            }
        )
    }

    if (reservaAReprogramar != null && !mostrarMapaAsientos && !mostrarSeleccionPasajeros) {
        DialogoReprogramacion(
            reservaOriginal = reservaAReprogramar!!,
            ciudades = ciudadesDisponibles,
            viewModel = viewModel,
            onVerAsientos = { nuevaRuta, nuevaFecha, nuevoHorario ->
                scope.launch {
                    val ocupados = viewModel.buscarAsientosOcupados(nuevaRuta, nuevaFecha, nuevoHorario)
                    asientosOcupadosDestino = ocupados
                    datosNuevoViaje = Triple(nuevaRuta, nuevaFecha, nuevoHorario)
                    pasajeroEnTransito = reservaAReprogramar

                    mostrarMapaAsientos = true
                }
            },
            onCancelar = { reservaAReprogramar = null }
        )
    }

    if (mostrarSeleccionPasajeros && reservaAReprogramar != null) {
        DialogoSeleccionPasajeros(
            pasajeros = reservaAReprogramar!!.pasajeros,
            onConfirmar = { seleccionados ->
                pasajerosSeleccionadosParaMover = seleccionados
                mostrarSeleccionPasajeros = false
            },
            onCancelar = {
                mostrarSeleccionPasajeros = false
                reservaAReprogramar = null
            }
        )
    }
    if (mostrarMapaAsientos && pasajeroEnTransito != null && datosNuevoViaje != null) {
        val (nuevaRuta, nuevaFecha, nuevoHorario) = datosNuevoViaje!!


        LaunchedEffect(key1 = nuevaRuta, key2 = nuevaFecha, key3 = nuevoHorario) {
            viewModel.escucharAsientosEnVivo(nuevaRuta, nuevaFecha, nuevoHorario).collect { lista ->
                asientosEnVivo = lista
            }
        }

        MapaAsientosReprogramacion(
            ruta = nuevaRuta,
            fecha = nuevaFecha,
            horario = nuevoHorario,
            asientosOcupados = asientosEnVivo,
            asientosNecesarios = if (pasajerosSeleccionadosParaMover.isNotEmpty()) pasajerosSeleccionadosParaMover.size else pasajeroEnTransito!!.asientosComprados,
            capacidadVehiculo = 13,
            viewModel = viewModel,
            onConfirmarAsientos = { nuevosAsientosSeleccionados ->
                mensajeSistema = "Sincronizando con Firebase..."

                if (pasajerosSeleccionadosParaMover.size == pasajeroEnTransito!!.pasajeros.size) {

                    viewModel.ejecutarTraspasoDefinitivo(
                        rutaAntigua = pasajeroEnTransito!!.ruta,
                        fechaAntigua = pasajeroEnTransito!!.fecha,
                        horarioAntiguo = reservaAReprogramar!!.horario,
                        idReserva = pasajeroEnTransito!!.idReserva,
                        rutaNueva = nuevaRuta,
                        fechaNueva = nuevaFecha,
                        horarioNuevo = nuevoHorario,
                        nuevosAsientos = nuevosAsientosSeleccionados
                    ) { exito ->
                        if (exito) {
                            mensajeSistema = " ¡Traspaso Exitoso!"
                            mostrarMapaAsientos = false
                            pasajeroEnTransito = null
                            datosNuevoViaje = null
                            reservaAReprogramar = null
                            pasajerosSeleccionadosParaMover = emptyList()
                        } else {
                            mensajeSistema = "Error en Firebase. Intenta de nuevo."
                        }
                    }

                } else {

                    viewModel.ejecutarDivisionDeGrupo(
                        rutaAntigua = pasajeroEnTransito!!.ruta,
                        fechaAntigua = pasajeroEnTransito!!.fecha,
                        horarioAntiguo = reservaAReprogramar!!.horario,
                        idReservaOriginal = pasajeroEnTransito!!.idReserva,
                        pasajerosAMover = pasajerosSeleccionadosParaMover,
                        rutaNueva = nuevaRuta,
                        fechaNueva = nuevaFecha,
                        horarioNuevo = nuevoHorario,
                        nuevosAsientos = nuevosAsientosSeleccionados
                    ) { exito ->
                        if (exito) {
                            mensajeSistema = " ¡División Exitosa! Se separó al pasajero en un nuevo viaje."
                            mostrarMapaAsientos = false
                            pasajeroEnTransito = null
                            datosNuevoViaje = null
                            reservaAReprogramar = null
                            pasajerosSeleccionadosParaMover = emptyList()
                        } else {
                            mensajeSistema = "Error al dividir el grupo."
                        }
                    }
                }
            },
            onCancelar = { asientosQueTenia ->
                asientosQueTenia.forEach {
                    viewModel.liberarAsiento(nuevaRuta, nuevaFecha, nuevoHorario, it)
                }
                mostrarMapaAsientos = false
            }
        )
    }

    mensajeSistema?.let { mensaje ->
        AlertDialog(
            onDismissRequest = {
                if (!mensaje.contains("Sincronizando")) mensajeSistema = null
            },
            title = { Text("Aviso del Sistema", fontWeight = FontWeight.Bold) },
            text = { Text(mensaje) },
            confirmButton = {
                if (!mensaje.contains("Sincronizando")) {
                    Button(onClick = { mensajeSistema = null }, colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro)) {
                        Text("Aceptar")
                    }
                }
            }
        )
    }
}
